import { Media } from "@/interfaces/media";
import { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import useCustomMutation from "./useCustomMutation";

interface Params {
  mediaId: string | number;
  collections: string[];
}

type Props = {
  params: Params;
  immediate?: boolean;
};

type UpdateMedia = {
  mediaId: string | number;
  expirationDate: string;
};

const getMedia = async (params: Params) => {
  if (!params) return;

  const { data } = await axios.get<Media[]>(`api/medias/${params.mediaId}`, {
    params: {
      collections: params.collections,
    },
  });
  return data;
};

const updateMedia = async (params: UpdateMedia) => {
  const { data } = await axios.put(`api/medias/${params.mediaId}`, {
    expirationDate: params.expirationDate,
  });

  return data;
};

const deleteMedia = async (params: Omit<Params, "collections">) => {
  const { data } = await axios.delete(`api/medias/${params.mediaId}`);
  return data;
};

const useMedia = (props: Props) => {
  const { params, immediate = false } = props || {};
  const toast = useToast();

  const { data, isLoading, refetch, isFetching, isError } = useQuery(
    ["media"],
    () => getMedia({ ...params }),
    {
      enabled: !!params && immediate,
      retry: false,
    },
  );

  const mediaUpdateMutation = useMutation(
    (MutationParams: MutationParams<UpdateMedia>) =>
      updateMedia(MutationParams.params),
    useCustomMutation(toast, "update"),
  );

  const mediaDeleteMutation = useMutation(
    (MutationParams: MutationParams<Omit<Params, "collections">>) =>
      deleteMedia(MutationParams.params),
    useCustomMutation(toast, "delete"),
  );

  return {
    data,
    isLoading,
    refetch,
    isFetching,
    isError,
    mediaUpdateMutation,
    mediaUpdateMediaAsync: mediaUpdateMutation.mutateAsync,
    mediaDeleteMutation,
    mediaDeleteMediaAsync: mediaDeleteMutation.mutateAsync,
  };
};

export default useMedia;
